import React, { useState } from "react";
import {
  Box, Button, Card, Typography, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Skeleton,
  Snackbar, Alert, InputAdornment, Tooltip, Checkbox, FormControlLabel, Switch,
  Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Paper, Grid, Avatar
} from "@mui/material";
import { Plus, Pencil, Trash2, X, Route, Type, Link2, AlignLeft, Tag, BookOpen, DollarSign, Clock, GraduationCap, Hash, Star, Search } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import {
  adminGetLearningPaths,
  adminGetLearningPathById,
  adminCreateLearningPath,
  adminUpdateLearningPath,
  adminDeleteLearningPath,
} from "../../api/learningPaths";
import { adminGetCourses } from "../../api/courses";

const STATUSES = ["DRAFT", "ACTIVE", "INACTIVE"];
const LEVELS = [
  { label: "Beginner",     value: "BEGINNER" },
  { label: "Intermediate", value: "INTERMEDIATE" },
  { label: "Professional", value: "PROFESSIONAL" },
  { label: "Expert",       value: "EXPERT" },
];

const STATUS_STYLES = {
  ACTIVE:   { bg: "rgba(34,197,94,0.1)",  color: "#16A34A" },
  DRAFT:    { bg: "rgba(245,158,11,0.1)", color: "#B45309" },
  INACTIVE: { bg: "rgba(239,68,68,0.1)",  color: "#DC2626" },
};

const LEVEL_STYLES = {
  BEGINNER:     { bg: "rgba(34,197,94,0.08)",   color: "#15803D" },
  INTERMEDIATE: { bg: "rgba(245,158,11,0.08)",  color: "#B45309" },
  PROFESSIONAL: { bg: "rgba(239,68,68,0.08)",   color: "#DC2626" },
  EXPERT:       { bg: "rgba(139,92,246,0.08)",  color: "#7C3AED" },
};

const generateSlug = (title) =>
  title.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

function extractErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (data && typeof data === "object") {
    const first = Object.values(data)[0];
    return Array.isArray(first) ? first[0] : String(first);
  }
  return fallback;
}

const DEFAULT_VALUES = {
  title: "",
  slug: "",
  short_tagline: "",
  description: "",
  salary_min: "",
  salary_max: "",
  duration_weeks: "",
  level: "BEGINNER",
  placement_support: false,
  highlight_badges: "",
  order: 0,
  is_featured: false,
  status: "DRAFT",
  card_image: null,
};

export default function LearningPathsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [thumbPreview, setThumbPreview] = useState(null);

  const { register, handleSubmit, reset, watch, control, formState: { errors } } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const watchTitle = watch("title", "");
  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const { data: paths = [], isLoading } = useQuery({
    queryKey: ["admin-learning-paths"],
    queryFn: () => adminGetLearningPaths().then(r => r.data),
  });

  const { data: courses = [], isLoading: coursesLoading } = useQuery({
    queryKey: ["admin-courses"],
    queryFn: () => adminGetCourses().then(r => r.data),
  });

  const createMutation = useMutation({
    mutationFn: adminCreateLearningPath,
    onSuccess: () => {
      qc.invalidateQueries(["admin-learning-paths"]);
      notify("Learning path created");
      closeDialog();
    },
    onError: (err) => notify(extractErrorMessage(err, "Couldn't create learning path"), "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateLearningPath(id, data),
    onSuccess: () => {
      qc.invalidateQueries(["admin-learning-paths"]);
      notify("Learning path updated");
      closeDialog();
    },
    onError: (err) => notify(extractErrorMessage(err, "Couldn't update learning path"), "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeleteLearningPath,
    onSuccess: () => {
      qc.invalidateQueries(["admin-learning-paths"]);
      notify("Learning path deleted");
      setConfirmDelete(null);
    },
    onError: (err) => {
      notify(extractErrorMessage(err, "Couldn't delete learning path"), "error");
      setConfirmDelete(null);
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (values) => {
    const fd = new FormData();
    fd.append("title", values.title);
    fd.append("slug", values.slug?.trim() || generateSlug(values.title));
    fd.append("short_tagline", values.short_tagline || "");
    fd.append("description", values.description || "");
    fd.append("level", values.level || "BEGINNER");
    fd.append("placement_support", values.placement_support ? "true" : "false");
    fd.append("is_featured", values.is_featured ? "true" : "false");
    fd.append("highlight_badges", values.highlight_badges || "");
    fd.append("order", values.order !== "" && values.order != null ? values.order : 0);
    fd.append("status", values.status || "DRAFT");

    if (values.salary_min !== "" && values.salary_min != null) {
      fd.append("salary_min", values.salary_min);
    }
    if (values.salary_max !== "" && values.salary_max != null) {
      fd.append("salary_max", values.salary_max);
    }
    if (values.duration_weeks !== "" && values.duration_weeks != null) {
      fd.append("duration_weeks", values.duration_weeks);
    }

    if (values.card_image?.[0]) {
      fd.append("card_image", values.card_image[0]);
    }

    if (editing) {
      updateMutation.mutate({ id: editing.id, data: fd });
    } else {
      createMutation.mutate(fd);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setThumbPreview(null);
    reset(DEFAULT_VALUES);
    setOpen(true);
  };

  const openEdit = async (p) => {
    setEditLoading(true);
    try {
      const { data } = await adminGetLearningPathById(p.id);
      setEditing(data);
      setThumbPreview(data.card_image_url || null);
      reset({
        title: data.title ?? "",
        slug: data.slug ?? "",
        short_tagline: data.short_tagline ?? "",
        description: data.description ?? "",
        salary_min: data.salary_min ?? "",
        salary_max: data.salary_max ?? "",
        duration_weeks: data.duration_weeks ?? "",
        level: data.level ?? "BEGINNER",
        placement_support: data.placement_support ?? false,
        highlight_badges: data.highlight_badges ?? "",
        order: data.order ?? 0,
        is_featured: data.is_featured ?? false,
        status: data.status ?? "DRAFT",
      });
      setOpen(true);
    } catch (err) {
      notify(extractErrorMessage(err, "Couldn't load learning path details"), "error");
    } finally {
      setEditLoading(false);
    }
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false);
    setEditing(null);
    setThumbPreview(null);
    reset(DEFAULT_VALUES);
  };

  const handleThumbChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setThumbPreview(URL.createObjectURL(file));
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const filteredPaths = paths.filter(p =>
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.short_tagline?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const paginatedPaths = filteredPaths.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalCount = filteredPaths.length;

  return (
    <AdminShell title="Learning Paths">
      {/* Page header */}
      <Box sx={{
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5, mb: 2.5,
      }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
          Curated course bundles shown on the public site. Assign courses to guide learners through a sequence.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{
            borderRadius: "10px", textTransform: "none", fontWeight: 700,
            bgcolor: "#2563EB", boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)",
            flexShrink: 0, "&:hover": { bgcolor: "#1D4ED8" },
          }}
        >
          Add Learning Path
        </Button>
      </Box>

      {/* Search Bar */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        p: 2, mb: 2.5,
      }}>
        <TextField
          fullWidth
          placeholder="Search learning paths by title, tagline, or description..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#94A3B8" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              border: "1px solid rgba(15,23,42,0.15)",
              "&:hover": { borderColor: "#2563EB" },
            },
          }}
        />
      </Card>

      {/* Table Card */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        overflow: "hidden"
      }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Learning Path", "Slug", "Level", "Duration", "Courses", "Status", ""].map(h => (
                  <TableCell key={h} sx={{
                    fontWeight: 700, fontSize: 12, letterSpacing: "0.03em",
                    textTransform: "uppercase", color: "text.secondary",
                    borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap"
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 200 : j === 4 ? 140 : 80} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedPaths.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box sx={{
                      width: 52, height: 52, borderRadius: "14px",
                      bgcolor: "rgba(37,99,235,0.07)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto", mb: 1.5
                    }}>
                      <Route size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>
                      No learning paths found
                    </Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery ? "Try adjusting your search" : "Group courses into a guided path for the public site."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedPaths.map((p) => {
                const statusStyle = STATUS_STYLES[p.status] || STATUS_STYLES.DRAFT;
                const levelStyle = LEVEL_STYLES[p.level] || {};
                return (
                  <TableRow key={p.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ minWidth: 240 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{
                          width: 36, height: 36, borderRadius: "9px",
                          bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0
                        }}>
                          <Route size={16} strokeWidth={2.1} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A", lineHeight: 1.3 }} noWrap>
                            {p.title}
                          </Typography>
                          {p.short_tagline && (
                            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.2 }} noWrap>
                              {p.short_tagline}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 160 }}>
                      {p.slug ? (
                        <Typography sx={{
                          fontSize: 12.5, fontFamily: "monospace", color: "rgba(15,23,42,0.5)",
                          bgcolor: "rgba(15,23,42,0.04)", px: 1, py: 0.35, borderRadius: "6px",
                          display: "inline-block", maxWidth: 150, overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap"
                        }}>
                          /{p.slug}
                        </Typography>
                      ) : "—"}
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {p.level ? (
                        <Chip
                          label={LEVELS.find(l => l.value === p.level)?.label || p.level}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: 11, height: 22, bgcolor: levelStyle.bg, color: levelStyle.color }}
                        />
                      ) : "—"}
                    </TableCell>

                    <TableCell sx={{ fontSize: 13.5, color: "text.secondary", whiteSpace: "nowrap" }}>
                      {p.duration_weeks != null ? `${p.duration_weeks} wk${p.duration_weeks !== 1 ? "s" : ""}` : "—"}
                    </TableCell>

                    <TableCell sx={{ minWidth: 180 }}>
                      {p.courses?.length > 0 ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                          {p.courses.slice(0, 3).map(c => (
                            <Chip key={c.id} label={c.title} size="small" variant="outlined" sx={{
                              fontSize: 11, fontWeight: 600, borderColor: "rgba(15,23,42,0.12)",
                              color: "text.secondary", height: 20
                            }} />
                          ))}
                          {p.courses.length > 3 && (
                            <Chip label={`+${p.courses.length - 3} more`} size="small" sx={{
                              fontSize: 11, fontWeight: 600, bgcolor: "rgba(37,99,235,0.07)", color: "#2563EB", height: 20
                            }} />
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: 13, color: "rgba(15,23,42,0.35)", fontStyle: "italic" }}>
                          No courses assigned
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip label={p.status || "DRAFT"} size="small" sx={{
                        fontWeight: 700, fontSize: 11, height: 22,
                        bgcolor: statusStyle.bg, color: statusStyle.color
                      }} />
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openEdit(p)}
                              disabled={editLoading}
                              sx={{
                                border: "1px solid rgba(15,23,42,0.1)",
                                borderRadius: "8px",
                                "&:hover": { borderColor: "#2563EB", color: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" }
                              }}
                            >
                              {editLoading ? <CircularProgress size={14} /> : <Pencil size={14} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setConfirmDelete(p)}
                            sx={{
                              border: "1px solid rgba(15,23,42,0.1)",
                              borderRadius: "8px",
                              "&:hover": { borderColor: "#EF4444", color: "#EF4444", bgcolor: "rgba(239,68,68,0.05)" }
                            }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {/* Pagination Controls */}
        {!isLoading && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid rgba(15,23,42,0.08)",
              "& .MuiTablePagination-toolbar": { padding: "16px 24px" },
              "& .MuiTablePagination-selectLabel": {
                fontSize: "13.5px", color: "text.secondary", marginRight: "8px",
              },
              "& .MuiTablePagination-displayedRows": { fontSize: "13.5px", color: "text.secondary" },
              "& .MuiInputBase-root": {
                borderRadius: "8px", border: "1px solid rgba(15,23,42,0.15)",
              },
              "& .MuiTablePagination-actions": { gap: "4px" },
              "& .MuiIconButton-root": {
                borderRadius: "8px", border: "1px solid rgba(15,23,42,0.1)", width: "32px", height: "32px",
              },
            }}
          />
        )}
      </Card>

      {/* Enhanced Create/Edit Dialog */}
      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            maxHeight: "90vh",
            overflow: "hidden",
            boxShadow: "0 30px 70px rgba(15,23,42,0.22)",
          }
        }}
      >
        <DialogTitle sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2.5,
          px: 3,
          background: "linear-gradient(135deg, #0891B2 0%, #2563EB 55%, #1D4ED8 100%)",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <Route size={22} color="#FFFFFF" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 21, color: "#FFFFFF", letterSpacing: "-0.01em" }}>
                {editing ? "Edit Learning Path" : "Add New Learning Path"}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.85)", fontWeight: 500, mt: 0.25 }}>
                {editing ? `Editing: ${editing.title}` : "Group courses into a guided sequence"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            pt: 3,
            pb: 1,
            px: 3,
            maxHeight: "65vh",
            overflowY: "auto",
            backgroundColor: "#F1F5F9",
          }}>
            {/* Basic Information Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #0891B2",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(8,145,178,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Type size={17} color="#0891B2" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Basic Information
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Title"
                    fullWidth
                    disabled={saving}
                    required
                    error={!!errors.title}
                    helperText={errors.title ? "Title is required" : ""}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Type size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("title", { required: true })}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Slug"
                    fullWidth
                    disabled={saving}
                    placeholder={watchTitle ? generateSlug(watchTitle) : "auto-generated from title"}
                    helperText="Leave blank to auto-generate. Lowercase letters, numbers, hyphens only."
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Link2 size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("slug")}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Short Tagline"
                    fullWidth
                    disabled={saving}
                    placeholder="A one-line hook for the learning path"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Tag size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("short_tagline")}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={4}
                    disabled={saving}
                    placeholder="Detailed overview of what learners will achieve"
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                        <AlignLeft size={16} color="#94A3B8" />
                      </InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("description")}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Career & Duration Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #7C3AED",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(124,58,237,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <DollarSign size={17} color="#7C3AED" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Career & Duration
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <Controller
                    name="level"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        label="Level"
                        fullWidth
                        disabled={saving}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><GraduationCap size={16} color="#94A3B8" /></InputAdornment>
                        }}
                        InputLabelProps={{ shrink: true }}
                        {...field}
                        value={field.value ?? ""}
                      >
                        {LEVELS.map(l => (
                          <MenuItem key={l.value} value={l.value}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Box sx={{
                                width: 8, height: 8, borderRadius: "50%",
                                bgcolor: LEVEL_STYLES[l.value]?.bg || "rgba(37,99,235,0.15)"
                              }} />
                              {l.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField
                    label="Duration (weeks)"
                    fullWidth
                    disabled={saving}
                    type="number"
                    placeholder="e.g. 12"
                    inputProps={{ min: 1, step: 1 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Clock size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("duration_weeks", { min: 1 })}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField
                    label="Salary Min (₹)"
                    fullWidth
                    disabled={saving}
                    type="number"
                    placeholder="e.g. 500000"
                    inputProps={{ min: 0, step: 1 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("salary_min", { min: 0 })}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField
                    label="Salary Max (₹)"
                    fullWidth
                    disabled={saving}
                    type="number"
                    placeholder="e.g. 1500000"
                    inputProps={{ min: 0, step: 1 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("salary_max", { min: 0 })}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Highlight Badges"
                    fullWidth
                    disabled={saving}
                    placeholder='e.g. "Top Rated", "Job Ready"'
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Star size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("highlight_badges")}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Display Settings Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #16A34A",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(22,163,74,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Tag size={17} color="#16A34A" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Display Settings
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        label="Status"
                        fullWidth
                        disabled={saving}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Tag size={16} color="#94A3B8" /></InputAdornment>
                        }}
                        InputLabelProps={{ shrink: true }}
                        {...field}
                        value={field.value ?? ""}
                      >
                        {STATUSES.map(s => (
                          <MenuItem key={s} value={s}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Box sx={{
                                width: 8, height: 8, borderRadius: "50%",
                                bgcolor: STATUS_STYLES[s]?.bg || "rgba(15,23,42,0.08)"
                              }} />
                              {s}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField
                    label="Display Order"
                    type="number"
                    fullWidth
                    disabled={saving}
                    placeholder="0 = first"
                    inputProps={{ min: 0, step: 1 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Hash size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("order", { min: 0 })}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <Controller
                    name="placement_support"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!field.value}
                            onChange={e => field.onChange(e.target.checked)}
                            disabled={saving}
                            color="primary"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#2563EB' },
                              '& .MuiSwitch-track': { backgroundColor: 'rgba(37,99,235,0.15)' },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <BookOpen size={16} color="#94A3B8" />
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
                              Placement Support
                            </Typography>
                          </Box>
                        }
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <Controller
                    name="is_featured"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!field.value}
                            onChange={e => field.onChange(e.target.checked)}
                            disabled={saving}
                            color="primary"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#2563EB' },
                              '& .MuiSwitch-track': { backgroundColor: 'rgba(37,99,235,0.15)' },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Star size={16} color="#94A3B8" />
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
                              Featured on homepage
                            </Typography>
                          </Box>
                        }
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Associated Courses Section (Read-only) */}
            {editing && editing.courses?.length > 0 && (
              <Paper elevation={0} sx={{
                p: 3,
                borderRadius: "16px",
                border: "1px solid rgba(15,23,42,0.06)",
                borderLeft: "4px solid #EA580C",
                backgroundColor: "#FFFFFF",
                boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: "9px",
                    backgroundColor: "rgba(234,88,12,0.1)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <BookOpen size={17} color="#EA580C" />
                  </Box>
                  <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                    Associated Courses (Read-only)
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1.5 }}>
                  Note: Course associations are managed separately. These are the currently linked courses.
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {editing.courses.map(c => (
                    <Chip
                      key={c.id}
                      label={c.title}
                      variant="outlined"
                      sx={{
                        fontSize: 12.5, fontWeight: 600, borderColor: "rgba(15,23,42,0.12)",
                        color: "text.secondary", height: 28, borderRadius: "8px"
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Thumbnail Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #2563EB",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(37,99,235,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Route size={17} color="#2563EB" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Thumbnail
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: "span 12" }}>
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    borderRadius: "12px",
                    border: "2px dashed rgba(37,99,235,0.2)",
                    backgroundColor: "rgba(37,99,235,0.03)",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#2563EB",
                      backgroundColor: "rgba(37,99,235,0.05)"
                    }
                  }}>
                    <Avatar
                      variant="rounded"
                      src={thumbPreview || undefined}
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "12px",
                        bgcolor: "rgba(37,99,235,0.08)",
                        color: "#2563EB",
                        flexShrink: 0
                      }}
                    >
                      <Route size={24} />
                    </Avatar>
                    <Box>
                      <Button
                        component="label"
                        variant="contained"
                        size="small"
                        disabled={saving}
                        startIcon={<BookOpen size={14} />}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: "8px",
                          bgcolor: "#2563EB",
                          "&:hover": { bgcolor: "#1D4ED8" }
                        }}
                      >
                        {thumbPreview ? "Change Image" : "Upload Image"}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          {...register("card_image")}
                          onChange={(e) => {
                            register("card_image").onChange(e);
                            handleThumbChange(e);
                          }}
                        />
                      </Button>
                      <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                        Recommended: 400x250px, JPG/PNG
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </DialogContent>

          <DialogActions sx={{
            px: 3, py: 2.25,
            borderTop: "1px solid rgba(15,23,42,0.08)",
            backgroundColor: "#FFFFFF",
            display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5,
          }}>
            <Button
              onClick={closeDialog}
              disabled={saving}
              sx={{
                textTransform: "none", fontWeight: 700, color: "#475569",
                px: 2.5, py: 1, borderRadius: "10px",
                backgroundColor: "rgba(15,23,42,0.04)",
                "&:hover": { backgroundColor: "rgba(15,23,42,0.08)" },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "10px",
                background: "linear-gradient(135deg, #0891B2 0%, #2563EB 100%)",
                px: 3.5, py: 1.1, minWidth: 140,
                boxShadow: "0 4px 14px rgba(8,145,178,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0E7490 0%, #1D4ED8 100%)",
                  boxShadow: "0 6px 18px rgba(8,145,178,0.4)"
                },
              }}
            >
              {saving ? "Saving..." : editing ? "Update Learning Path" : "Create Learning Path"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => !deleteMutation.isPending && setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: "1px solid rgba(15,23,42,0.08)",
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Delete this learning path?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            "{confirmDelete?.title}" will be removed from the public site immediately. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          px: 3, pb: 2.5,
          borderTop: "1px solid rgba(15,23,42,0.08)",
        }}>
          <Button
            onClick={() => setConfirmDelete(null)}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending}
            variant="contained"
            startIcon={deleteMutation.isPending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: "10px",
              bgcolor: "#EF4444", px: 2.5, minWidth: 100,
              "&:hover": { bgcolor: "#DC2626" }
            }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}